import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, DownloadSimple, Envelope, Star, FileText, Copy, Check } from "@phosphor-icons/react";
import { api, absoluteUrl } from "@/lib/api";
import { toast } from "sonner";

const LOGO_URL =
  "/vznlogo.png";

const SERVICE_LABEL = {
  flyer: "Flyers",
  logo: "Logos",
  seal: "Seals",
  banner: "Banners",
  web: "Web",
  other: "Other",
};

export default function Press() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    document.title = "Press kit — Vision Grafix";
    api
      .get("/press/kit")
      .then(({ data }) => setData(data))
      .catch(() => setData(false));
  }, []);

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(null), 1600);
    } catch (_) {
      window.prompt(`Copy ${label}:`, value);
    }
  };

  if (data === null) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-xs text-[#a3a3a3] uppercase tracking-[0.3em]">Loading press kit…</div>
      </div>
    );
  }
  if (data === false) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-sm text-[#a3a3a3]">Press kit unavailable. <Link to="/" className="text-[#FF2D2D] underline">Go home</Link></div>
      </div>
    );
  }

  const { studio, stats, services, featured, logos } = data;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 text-xs text-[#a3a3a3] hover:text-white transition-colors" data-testid="press-back-home">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <img src={LOGO_URL} alt="Vision Grafix" className="h-7 md:h-8 w-auto object-contain" />
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D] mb-4">Press kit</div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95]">
              {studio.name}.<br />
              <span className="text-[#a3a3a3]">{studio.tagline}</span>
            </h1>
            <p className="text-sm md:text-base text-[#a3a3a3] mt-8 max-w-2xl leading-relaxed" data-testid="press-bio">
              {studio.bio}
            </p>
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
            <Stat label="Delivered" value={stats.delivered_projects} testId="stat-delivered" />
            <Stat
              label="Avg rating"
              value={stats.avg_rating != null ? stats.avg_rating.toFixed(1) : "—"}
              icon={<Star size={11} weight="fill" className="text-[#FF2D2D]" />}
              testId="stat-rating"
            />
            <Stat label="Reviews" value={stats.review_count} testId="stat-reviews" />
            <Stat label="Services" value={stats.services_offered} testId="stat-services" />
          </div>
        </div>
      </section>

      {/* Quick facts */}
      <section className="border-b border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D] mb-6">At a glance</div>
          <dl className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#262626] border border-[#262626]">
            <Fact term="Founded" def={String(studio.founded_year)} />
            <Fact term="Website" def={studio.url.replace(/^https?:\/\//, "")} />
            <Fact term="General" def={studio.contact_email} onCopy={() => copy("Email", studio.contact_email)} copied={copied === "Email"} />
            <Fact term="Press" def={studio.press_email} onCopy={() => copy("Press email", studio.press_email)} copied={copied === "Press email"} />
          </dl>
        </div>
      </section>

      {/* Services */}
      {services.length > 0 && (
        <section className="border-b border-[#262626]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D] mb-6">Services delivered</div>
            <div className="flex flex-wrap gap-2" data-testid="press-services">
              {services.map((s) => (
                <div key={s.type} className="border border-[#262626] px-4 py-2 text-xs flex items-center gap-3">
                  <span className="text-white font-semibold">{SERVICE_LABEL[s.type] || s.type.replace(/_/g, " ")}</span>
                  <span className="text-[#a3a3a3] tabular-nums">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured work */}
      {featured.length > 0 && (
        <section className="border-b border-[#262626]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Featured work</div>
                <h2 className="font-display text-3xl md:text-4xl mt-2">Selected pieces.</h2>
              </div>
              <Link to="/#work" className="text-xs text-[#a3a3a3] hover:text-[#FF2D2D] flex items-center gap-1 uppercase tracking-wider">
                Full portfolio <ArrowUpRight size={12} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="press-featured">
              {featured.map((it) => (
                <div key={it.id} className="border border-[#262626] bg-[#0a0a0a] overflow-hidden group">
                  {it.cover_url ? (
                    <div className="aspect-[4/3] overflow-hidden bg-[#0a0a0a]">
                      <img
                        src={`${absoluteUrl(it.cover_url)}?w=600`}
                        alt={it.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-[#141414] flex items-center justify-center text-[#404040]">
                      <FileText size={28} />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">
                      {SERVICE_LABEL[it.service_type] || it.service_type}
                    </div>
                    <div className="font-display text-lg mt-1">{it.title}</div>
                    {it.client_name && (
                      <div className="text-xs text-[#a3a3a3] mt-1">For {it.client_name}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Logo bundle */}
      <section className="border-b border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D] mb-6">Logo</div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="border border-[#262626] bg-[#0a0a0a] aspect-[16/9] flex items-center justify-center p-12">
              <img src={LOGO_URL} alt="Vision Grafix wordmark" className="max-h-32 w-auto object-contain" />
            </div>
            <div className="space-y-3">
              {logos.map((l) => (
                <a
                  key={l.url}
                  href={l.url}
                  download
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`press-logo-${l.format}`}
                  className="flex items-center justify-between gap-4 p-4 border border-[#262626] bg-[#0a0a0a] hover:border-[#FF2D2D] hover:text-[#FF2D2D] transition-colors group"
                >
                  <div>
                    <div className="text-sm font-semibold">{l.label}</div>
                    <div className="text-xs text-[#a3a3a3] uppercase tracking-wider mt-1">.{l.format}</div>
                  </div>
                  <DownloadSimple size={20} className="shrink-0 group-hover:translate-y-0.5 transition-transform" />
                </a>
              ))}
              <div className="text-xs text-[#666] mt-4 leading-relaxed">
                Need additional formats (SVG, mono, dark/light variants), product screenshots, or
                team headshots? Drop us a note at{" "}
                <button onClick={() => copy("Press email", studio.press_email)} className="text-[#FF2D2D] hover:underline">
                  {studio.press_email}
                </button>.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="border border-[#262626] bg-gradient-to-br from-[#0a0a0a] to-[#050505] p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Press inquiries</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3">Talk to a human.</h2>
              <p className="text-sm text-[#a3a3a3] mt-3 max-w-md">
                We respond to journalist, partner, and agency requests within one business day.
                Include your outlet, deadline, and what assets you need.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a
                href={`mailto:${studio.press_email}`}
                data-testid="press-email-cta"
                className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2"
              >
                <Envelope size={14} weight="fill" /> {studio.press_email}
              </a>
              <button
                onClick={() => copy("Press email", studio.press_email)}
                className="btn-outline px-4 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2"
              >
                {copied === "Press email" ? <Check size={14} weight="bold" /> : <Copy size={14} />}
                {copied === "Press email" ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 flex items-center justify-between gap-4 text-xs text-[#a3a3a3]">
          <span>© {new Date().getFullYear()} Vision Grafix</span>
          <Link to="/" className="hover:text-white">Visit the studio →</Link>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, icon, testId }) {
  return (
    <div className="bg-[#050505] p-5" data-testid={testId}>
      <div className="font-display text-3xl md:text-4xl tabular-nums flex items-center gap-1.5">
        {value}
        {icon}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3] mt-1">{label}</div>
    </div>
  );
}

function Fact({ term, def, onCopy, copied }) {
  return (
    <div className="bg-[#050505] p-5">
      <dt className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">{term}</dt>
      <dd className="text-sm mt-2 flex items-center gap-2 break-all">
        <span className="text-white">{def}</span>
        {onCopy && (
          <button
            type="button"
            onClick={onCopy}
            className="text-[#a3a3a3] hover:text-[#FF2D2D] shrink-0"
            aria-label={`Copy ${term}`}
          >
            {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
          </button>
        )}
      </dd>
    </div>
  );
}
